import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { Loader2, Package, Plus, Trash2, ArrowUp, ArrowDown, Calendar, FileText, Truck, Printer, Send, Unlock } from 'lucide-react';
import type { Database } from '../types/database.types';
import ProductSelectionModal from '../components/ProductSelectionModal';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import {
  calculateInvoiceTotals,
  canIssueDraftOnFattureInCloud,
  issueDraftOnFattureInCloud,
  type DocumentType,
} from '../lib/invoiceUtils';
import { notifySubscriptionDraftsChanged } from '../lib/subscriptionInvoiceUtils';
import { resolveNoVatClauseText } from '../lib/noVatClause';
import { InvoicePrintView } from './InvoicePrintView';
import { FloatingWindow } from '../components/ui/floating-window';
import { COMPANY_INFO } from '../lib/letterheadPdf';
import { fetchInstallments, markInstallmentPaid, recomputeInstallmentAmounts } from '../lib/priceListInvoiceUtils';
import { formatCurrency } from '../lib/exportUtils';

type Invoice = Database['public']['Tables']['invoices']['Row'];
type InvoiceItem = Database['public']['Tables']['invoice_items']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

interface Product {
  id: string;
  code: string;
  name: string;
  category?: string;
  unit: string;
  // I prodotti importati da Fatture in Cloud possono non avere prezzo.
  base_price: number | null;
  photo_url?: string | null;
}

interface InvoiceWithItems extends Invoice {
  customers?: Customer;
  invoice_items: (InvoiceItem & {
    products: Product | null;
  })[];
  carriers?: { id: string; name: string } | null;
  package_types?: { id: string; name: string } | null;
}

type InvoiceItemWithProduct = InvoiceWithItems['invoice_items'][number];

interface InvoiceDetailPageProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId?: string;
  mode?: 'view' | 'edit';
  onSaveSuccess: () => void;
}

/** Dopo quanti minuti l'admin puo' togliere il lucchetto a una bozza rimasta
 *  "in invio": prima di allora la risposta di Fatture in Cloud puo' ancora
 *  arrivare. */
const UNLOCK_AFTER_MINUTES = 10;

const getDocumentTypeLabel = (type: DocumentType | string): string => {
  switch (type) {
    case 'invoice':
      return 'Fattura';
    case 'invoice_with_delivery':
      return 'Fattura Accompagnatoria';
    case 'proforma':
      return 'Fattura Pro Forma';
    case 'ddt':
      return 'DDT';
    case 'credit_note':
      return 'Nota di Credito';
    default:
      return type;
  }
};

export function InvoiceDetailPage({
  isOpen,
  onClose,
  invoiceId,
  mode = 'view',
  onSaveSuccess,
}: InvoiceDetailPageProps) {
  const { addNotification } = useNotifications();
  const { user, profile } = useAuth();

  const productsSectionRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<InvoiceWithItems | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [sortField, setSortField] = useState<'code' | 'name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pendingQuantityValues, setPendingQuantityValues] = useState<Record<string, string>>({});
  const [pendingPriceValues, setPendingPriceValues] = useState<Record<string, string>>({});
  const [pendingDiscountValues, setPendingDiscountValues] = useState<Record<string, string>>({});
  const [pendingVatValues, setPendingVatValues] = useState<Record<string, string>>({});
  const [invoiceDate, setInvoiceDate] = useState<string>('');
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [paymentMethods, setPaymentMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [showPrintView, setShowPrintView] = useState(false);
  const [installments, setInstallments] = useState<Database['public']['Tables']['invoice_installments']['Row'][]>([]);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [confirmingIssue, setConfirmingIssue] = useState(false);
  const [issuing, setIssuing] = useState(false);

  // Lucchetto d'invio: la bozza e' partita per Fatture in Cloud e la risposta
  // non e' ancora tornata (o non tornera' piu', se la rete e' caduta).
  const claimedAt = currentInvoice?.fic_claimed_at ?? null;
  const isClaimed = !!claimedAt && !currentInvoice?.fic_id;
  const claimAgeMinutes = claimedAt ? (Date.now() - new Date(claimedAt).getTime()) / 60_000 : 0;
  const canUnlock = isClaimed && profile?.role === 'admin' && claimAgeMinutes >= UNLOCK_AFTER_MINUTES;
  const canIssue = !!currentInvoice && canIssueDraftOnFattureInCloud(currentInvoice) && !isClaimed;
  // Un documento che vive su Fatture in Cloud prende da la' anche gli incassi.
  const incassiDaFic = !!currentInvoice?.fic_id;

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load invoice if editing
      if (invoiceId) {
        await fetchInvoiceDetails(invoiceId);
      }

      // Load payment methods
      const { data: pmData, error: pmError } = await supabase
        .from('payment_methods')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (!pmError && pmData) {
        setPaymentMethods(pmData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare i dati',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvoiceDetails = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers (*),
          invoice_items (
            *,
            products (id, code, name, category, unit, base_price, photo_url)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      const invoice = data as InvoiceWithItems;

      // Carica carrier e package_type separatamente se presenti
      if (invoice.carrier_id) {
        const { data: carrierData } = await supabase
          .from('carriers')
          .select('id, name')
          .eq('id', invoice.carrier_id)
          .single();
        if (carrierData) {
          invoice.carriers = carrierData;
        }
      }

      if (invoice.package_type_id) {
        const { data: packageTypeData } = await supabase
          .from('package_types')
          .select('id, name')
          .eq('id', invoice.package_type_id)
          .single();
        if (packageTypeData) {
          invoice.package_types = packageTypeData;
        }
      }

      setCurrentInvoice(invoice);
      
      // Set invoice date
      if (invoice.invoice_date) {
        const [year, month, day] = invoice.invoice_date.split('-');
        setInvoiceDate(`${day}/${month}/${year}`);
      } else {
        setInvoiceDate('');
      }
      
      setPaymentTerms(invoice.payment_terms || '');
      setNotes(invoice.notes || '');

      try {
        const inst = await fetchInstallments(invoice.id);
        setInstallments(inst as any);
      } catch (e) {
        console.warn('Errore caricamento rate:', e);
        setInstallments([]);
      }

    } catch (error) {
      console.error('Error fetching invoice details:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare i dettagli della fattura',
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset state quando cambia invoiceId o mode
      setCurrentInvoice(null);
      setPendingQuantityValues({});
      setPendingPriceValues({});
      setPendingDiscountValues({});
      setPendingVatValues({});
      setInvoiceDate('');
      setPaymentTerms('');
      setNotes('');
      setSelectedCategory('all');
      setSortField('name');
      setSortDirection('asc');
      
      loadData();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, invoiceId, mode]);

  const updateInvoiceTotals = async () => {
    if (!currentInvoice) return;

    try {
      // Calcola i totali da tutti gli invoice_items
      const { data: invoiceItems, error: itemsError } = await supabase
        .from('invoice_items')
        .select('quantity, unit_price, discount_percentage, vat_rate, line_total')
        .eq('invoice_id', currentInvoice.id);

      if (itemsError) throw itemsError;

      const itemsInput = invoiceItems?.map(item => ({
        product_id: null,
        description: '',
        quantity: item.quantity,
        unit: '',
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage,
        vat_rate: item.vat_rate,
      })) || [];

      const noVatForfettario = (currentInvoice as any).no_vat_forfettario !== false;
      const totals = calculateInvoiceTotals(itemsInput, noVatForfettario);

      // Aggiorna la fattura con i nuovi totali (incluso bollo auto)
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          subtotal_amount: totals.subtotal_amount,
          discount_amount: totals.discount_amount,
          taxable_amount: totals.taxable_amount,
          tax_amount: totals.tax_amount,
          total_amount: totals.total_amount,
          marca_da_bollo_amount: totals.marca_da_bollo_amount,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', currentInvoice.id);

      if (updateError) throw updateError;

      // Ricalcola gli importi delle rate (preservando % e gg) se la fattura ha un piano
      try {
        const res = await recomputeInstallmentAmounts(
          currentInvoice.id,
          totals.total_amount,
          currentInvoice.invoice_date
        );
        if (res.skipped && res.reason === 'has_paid_installments') {
          addNotification({
            type: 'warning',
            title: 'Rate non aggiornate',
            message: 'Ci sono rate già pagate: importi rate non ricalcolati. Verifica manualmente.',
          });
        }
      } catch (e) {
        console.warn('Errore ricalcolo rate:', e);
      }

      // Ricarica i dati della fattura
      await fetchInvoiceDetails(currentInvoice.id);
    } catch (error) {
      console.error('Errore nell\'aggiornamento totali fattura:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento dei totali',
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa riga dalla fattura?')) return;

    try {
      const { error } = await supabase
        .from('invoice_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Riga rimossa',
        message: 'La riga è stata rimossa dalla fattura',
      });

      // Aggiorna i totali e ricarica i dati
      await updateInvoiceTotals();
    } catch (error: any) {
      console.error('Error deleting invoice item:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore durante la rimozione della riga',
      });
    }
  };

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    try {
      if (newQuantity <= 0) {
        addNotification({
          type: 'warning',
          title: 'Attenzione',
          message: 'La quantità deve essere maggiore di zero',
        });
        return;
      }

      // Calcola il nuovo line_total
      const item = currentInvoice?.invoice_items.find(i => i.id === itemId);
      if (!item) return;

      const itemSubtotal = newQuantity * item.unit_price;
      const itemDiscount = (itemSubtotal * item.discount_percentage) / 100;
      const itemTaxable = itemSubtotal - itemDiscount;
      const itemTax = (itemTaxable * item.vat_rate) / 100;
      const newLineTotal = itemTaxable + itemTax;

      const { error } = await supabase
        .from('invoice_items')
        .update({ 
          quantity: newQuantity,
          line_total: newLineTotal,
        })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        const updatedItems = currentInvoice.invoice_items.map(item =>
          item.id === itemId ? { ...item, quantity: newQuantity, line_total: newLineTotal } : item
        );
        setCurrentInvoice({ ...currentInvoice, invoice_items: updatedItems });
      }

      // Pulisci il valore temporaneo
      setPendingQuantityValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });

      // Aggiorna i totali della fattura
      await updateInvoiceTotals();
    } catch (error) {
      console.error('Errore nell\'aggiornamento della quantità:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento della quantità',
      });
    }
  };

  const handlePriceChange = async (itemId: string, newPrice: number) => {
    try {
      const normalizedPrice = Number.isFinite(newPrice)
        ? Number(newPrice.toFixed(4))
        : 0;

      // Calcola il nuovo line_total
      const item = currentInvoice?.invoice_items.find(i => i.id === itemId);
      if (!item) return;

      const itemSubtotal = item.quantity * normalizedPrice;
      const itemDiscount = (itemSubtotal * item.discount_percentage) / 100;
      const itemTaxable = itemSubtotal - itemDiscount;
      const itemTax = (itemTaxable * item.vat_rate) / 100;
      const newLineTotal = itemTaxable + itemTax;

      const { error } = await supabase
        .from('invoice_items')
        .update({ 
          unit_price: normalizedPrice,
          line_total: newLineTotal,
        })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        const updatedItems = currentInvoice.invoice_items.map(item =>
          item.id === itemId ? { ...item, unit_price: normalizedPrice, line_total: newLineTotal } : item
        );
        setCurrentInvoice({ ...currentInvoice, invoice_items: updatedItems });
      }

      // Pulisci il valore temporaneo
      setPendingPriceValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });

      // Aggiorna i totali della fattura
      await updateInvoiceTotals();
    } catch (error) {
      console.error('Errore nell\'aggiornamento del prezzo:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento del prezzo',
      });
    }
  };

  const handleDiscountChange = async (itemId: string, newDiscount: number) => {
    try {
      const normalizedDiscount = Math.max(0, Math.min(100, newDiscount));

      // Calcola il nuovo line_total
      const item = currentInvoice?.invoice_items.find(i => i.id === itemId);
      if (!item) return;

      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = (itemSubtotal * normalizedDiscount) / 100;
      const itemTaxable = itemSubtotal - itemDiscount;
      const itemTax = (itemTaxable * item.vat_rate) / 100;
      const newLineTotal = itemTaxable + itemTax;

      const { error } = await supabase
        .from('invoice_items')
        .update({ 
          discount_percentage: normalizedDiscount,
          line_total: newLineTotal,
        })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        const updatedItems = currentInvoice.invoice_items.map(item =>
          item.id === itemId ? { ...item, discount_percentage: normalizedDiscount, line_total: newLineTotal } : item
        );
        setCurrentInvoice({ ...currentInvoice, invoice_items: updatedItems });
      }

      // Pulisci il valore temporaneo
      setPendingDiscountValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });

      // Aggiorna i totali della fattura
      await updateInvoiceTotals();
    } catch (error) {
      console.error('Errore nell\'aggiornamento dello sconto:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento dello sconto',
      });
    }
  };

  const handleVatChange = async (itemId: string, newVat: number) => {
    try {
      const normalizedVat = Math.max(0, Math.min(100, newVat));

      // Calcola il nuovo line_total
      const item = currentInvoice?.invoice_items.find(i => i.id === itemId);
      if (!item) return;

      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = (itemSubtotal * item.discount_percentage) / 100;
      const itemTaxable = itemSubtotal - itemDiscount;
      const itemTax = (itemTaxable * normalizedVat) / 100;
      const newLineTotal = itemTaxable + itemTax;

      const { error } = await supabase
        .from('invoice_items')
        .update({ 
          vat_rate: normalizedVat,
          vat_amount: itemTax,
          line_total: newLineTotal,
        })
        .eq('id', itemId);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        const updatedItems = currentInvoice.invoice_items.map(item =>
          item.id === itemId ? { ...item, vat_rate: normalizedVat, vat_amount: itemTax, line_total: newLineTotal } : item
        );
        setCurrentInvoice({ ...currentInvoice, invoice_items: updatedItems });
      }

      // Pulisci il valore temporaneo
      setPendingVatValues(prev => {
        if (!(itemId in prev)) return prev;
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      });

      // Aggiorna i totali della fattura
      await updateInvoiceTotals();
    } catch (error) {
      console.error('Errore nell\'aggiornamento dell\'IVA:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento dell\'IVA',
      });
    }
  };

  const handleInvoiceDateChange = async (value: string) => {
    setInvoiceDate(value);
    
    if (!currentInvoice) return;

    // Converti da formato italiano (dd/mm/yyyy) a ISO (yyyy-mm-dd)
    const parts = value.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const isoDate = `${year}-${month}-${day}`;

      try {
        const { error } = await supabase
          .from('invoices')
          .update({ invoice_date: isoDate })
          .eq('id', currentInvoice.id);

        if (error) throw error;

        // Aggiorna lo stato locale
        if (currentInvoice) {
          setCurrentInvoice({ ...currentInvoice, invoice_date: isoDate });
        }
      } catch (error) {
        console.error('Errore nell\'aggiornamento della data:', error);
        addNotification({
          type: 'error',
          title: 'Errore',
          message: 'Errore nell\'aggiornamento della data',
        });
      }
    }
  };

  const handlePaymentTermsChange = async (value: string) => {
    // Gestisci "none" come valore vuoto
    const actualValue = value === 'none' ? null : value;
    setPaymentTerms(actualValue || '');
    
    if (!currentInvoice) return;

    try {
      const { error } = await supabase
        .from('invoices')
        .update({ payment_terms: actualValue })
        .eq('id', currentInvoice.id);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        setCurrentInvoice({ ...currentInvoice, payment_terms: actualValue });
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento delle condizioni di pagamento:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento delle condizioni di pagamento',
      });
    }
  };

  const handleNotesChange = async (value: string) => {
    setNotes(value);
    
    if (!currentInvoice) return;

    try {
      const { error } = await supabase
        .from('invoices')
        .update({ notes: value || null })
        .eq('id', currentInvoice.id);

      if (error) throw error;

      // Aggiorna lo stato locale
      if (currentInvoice) {
        setCurrentInvoice({ ...currentInvoice, notes: value || null });
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento delle note:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nell\'aggiornamento delle note',
      });
    }
  };

  const handleProductsSelected = async (products: Product[]) => {
    if (!currentInvoice || products.length === 0) return;

    try {
      const itemsToInsert = products.map(product => {
        const quantity = 1;
        const unitPrice = product.base_price ?? 0;
        const discountPercentage = 0;
        const vatRate = 22;
        const subtotal = quantity * unitPrice;
        const discountAmount = (subtotal * discountPercentage) / 100;
        const taxable = subtotal - discountAmount;
        const tax = (taxable * vatRate) / 100;
        const lineTotal = taxable + tax;

        return {
          invoice_id: currentInvoice.id,
          product_id: product.id,
          description: product.name,
          quantity: quantity,
          unit: product.unit,
          unit_price: unitPrice,
          discount_percentage: discountPercentage,
          vat_rate: vatRate,
          vat_amount: tax,
          line_total: lineTotal,
        };
      });

      const { error } = await supabase
        .from('invoice_items')
        .insert(itemsToInsert);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Prodotti aggiunti',
        message: `${products.length} prodotto/i aggiunto/i alla fattura`,
      });

      // Aggiorna i totali e ricarica i dati
      await updateInvoiceTotals();
      onSaveSuccess();
    } catch (error: any) {
      console.error('Error adding products:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore durante l\'aggiunta dei prodotti',
      });
    }
  };

  const handleClose = () => {
    setCurrentInvoice(null);
    setPendingQuantityValues({});
    setPendingPriceValues({});
    setPendingDiscountValues({});
    setPendingVatValues({});
    setInvoiceDate('');
    setPaymentTerms('');
    setNotes('');
    onClose();
  };

  // La bozza diventa la fattura emessa: numero e data li assegna Fatture in
  // Cloud. Il doppio clic lo ferma il tasto disabilitato; due persone sulla
  // stessa bozza le ferma il lucchetto lato server.
  const handleIssueDraft = async () => {
    if (!currentInvoice || issuing) return;
    const id = currentInvoice.id;
    setIssuing(true);
    try {
      const issued = await issueDraftOnFattureInCloud(id);
      addNotification({
        type: 'success',
        title: `Fattura ${issued.invoice_number ?? ''} emessa`,
        message: "Creata su Fatture in Cloud. L'invio allo SDI si fa da Fatture in Cloud.",
      });
      onSaveSuccess();
    } catch (e) {
      addNotification({
        type: 'error',
        title: 'Emissione non riuscita',
        message: (e as Error)?.message ?? 'Fatture in Cloud non ha emesso la bozza.',
        persistent: true,
      });
    } finally {
      setIssuing(false);
      setConfirmingIssue(false);
      notifySubscriptionDraftsChanged();
      await fetchInvoiceDetails(id);
    }
  };

  // Toglie il lucchetto a una bozza rimasta "in invio". Solo dopo aver
  // controllato su Fatture in Cloud che il documento non sia nato: se c'e',
  // riemetterla creerebbe un secondo numero.
  const handleUnlockClaim = async () => {
    if (!currentInvoice || !canUnlock) return;
    const ok = confirm(
      'Prima di sbloccare, controlla su Fatture in Cloud che questo documento NON sia stato creato.\n\n' +
        "Se c'è già, non va riemesso: la sincronizzazione notturna lo porterà in Pixel e questa bozza andrà eliminata.\n\n" +
        'Sbloccare la bozza?'
    );
    if (!ok) return;
    const { error } = await supabase
      .from('invoices')
      .update({ fic_claimed_at: null, fic_claimed_by: null })
      .eq('id', currentInvoice.id)
      .is('fic_id', null);
    if (error) {
      addNotification({ type: 'error', title: 'Sblocco non riuscito', message: error.message });
      return;
    }
    addNotification({ type: 'success', title: 'Bozza sbloccata', message: 'Ora si può emettere di nuovo.' });
    await fetchInvoiceDetails(currentInvoice.id);
  };

  const handleDeleteInvoice = async () => {
    if (!currentInvoice || deletingInvoice) return;
    if (isClaimed) {
      addNotification({
        type: 'error',
        title: 'Bozza in invio',
        message: "Questa bozza è partita per Fatture in Cloud e l'esito non è ancora chiaro: non si elimina finché non viene sbloccata.",
      });
      return;
    }
    // Un documento con fic_id vive su Fatture in Cloud: cancellarlo solo qui
    // non servirebbe a niente, perche' la sincronizzazione notturna lo
    // riporterebbe dentro. E se e' gia' partito allo SDI non si cancella
    // affatto: si emette una nota di credito.
    if ((currentInvoice as any).fic_id) {
      addNotification({
        type: 'error',
        title: 'Non eliminabile da Pixel',
        message:
          'Questo documento vive su Fatture in Cloud. Va eliminato là: la sincronizzazione lo toglie poi anche da Pixel. Se è già stato inviato allo SDI non si elimina, si emette una nota di credito.',
      });
      return;
    }
    const numLabel = currentInvoice.invoice_number ?? `bozza ${currentInvoice.id.slice(0, 8)}`;
    // Una bozza di abbonamento eliminata non torna: la "prossima fattura" del
    // contratto e' gia' andata oltre quel periodo.
    const subscriptionNote = currentInvoice.subscription_id && currentInvoice.causale
      ? `\n\n${currentInvoice.causale}: questo periodo non verrà rifatturato in automatico. Per rifarlo, riporta la "Prossima fattura" del contratto all'inizio del periodo.`
      : '';
    if (!confirm(`Eliminare definitivamente la fattura ${numLabel}?\n\nVerranno cancellate righe e rate. L'operazione è irreversibile.${subscriptionNote}`)) {
      return;
    }
    setDeletingInvoice(true);
    try {
      const invoiceId = currentInvoice.id;
      const priceListId = (currentInvoice as any).price_list_id as string | null;

      const { error: instErr } = await supabase
        .from('invoice_installments')
        .delete()
        .eq('invoice_id', invoiceId);
      if (instErr) throw instErr;

      const { error: itemsErr } = await supabase
        .from('invoice_items')
        .delete()
        .eq('invoice_id', invoiceId);
      if (itemsErr) throw itemsErr;

      const { error: invErr } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);
      if (invErr) throw invErr;

      if (priceListId) {
        const { data: remaining, error: remErr } = await supabase
          .from('invoices')
          .select('id')
          .eq('price_list_id', priceListId)
          .limit(1);
        if (!remErr && (!remaining || remaining.length === 0)) {
          await (supabase.from('price_lists') as any)
            .update({ status: 'accepted' })
            .eq('id', priceListId);
        }
      }

      addNotification({
        type: 'success',
        title: 'Fattura eliminata',
        message: `${numLabel} eliminata. Offerta riportata in stato "accepted" se non aveva altre fatture.`,
      });
      notifySubscriptionDraftsChanged();
      onSaveSuccess();
      handleClose();
    } catch (e: any) {
      console.error('Error deleting invoice:', e);
      addNotification({
        type: 'error',
        title: 'Errore eliminazione',
        message: e?.message ?? 'Impossibile eliminare la fattura',
      });
    } finally {
      setDeletingInvoice(false);
    }
  };

  const renderProductCard = (item: InvoiceItemWithProduct) => {
    const itemSubtotal = item.quantity * item.unit_price;
    const itemDiscount = (itemSubtotal * item.discount_percentage) / 100;
    const itemTaxable = itemSubtotal - itemDiscount;
    const itemTax = (itemTaxable * item.vat_rate) / 100;
    const itemTotal = itemTaxable + itemTax;

    const product = item.products;

    return (
      <div key={item.id} className="bg-white border border-green-200 rounded p-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap xl:flex-nowrap">
          <div className="flex items-center gap-2 w-full min-w-0 sm:min-w-[220px] sm:flex-1">
            {product?.photo_url && (
              <img
                src={product.photo_url}
                alt={product.name}
                className="w-8 h-8 object-cover rounded border"
              />
            )}
            <div className="min-w-0">
              <p className="font-medium text-gray-900 text-xs break-words">{item.description}</p>
              {product && (
                <p className="text-xs text-gray-500">{product.code}</p>
              )}
            </div>
          </div>

          {item.discount_percentage > 0 && (
            <div className="text-xs text-green-700 font-semibold sm:min-w-[70px]">
              Sconto {item.discount_percentage}%
            </div>
          )}

          {mode === 'edit' && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:contents">
              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[120px]">
                <span className="text-xs text-gray-600 w-full sm:w-auto">Quantità:</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pendingQuantityValues[item.id] ?? item.quantity?.toString() ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (/^\d*\.?\d*$/.test(value)) {
                      setPendingQuantityValues(prev => ({
                        ...prev,
                        [item.id]: value
                      }));
                    }
                  }}
                  onBlur={() => {
                    const rawValue = pendingQuantityValues[item.id] ?? item.quantity?.toString() ?? '';
                    const parsedValue = parseFloat(rawValue);
                    if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue !== item.quantity) {
                      handleQuantityChange(item.id, parsedValue);
                    } else if (pendingQuantityValues[item.id] !== undefined) {
                      setPendingQuantityValues(prev => {
                        const { [item.id]: _removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-xs text-gray-500">{product?.unit || item.unit || ''}</span>
              </div>

              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[110px]">
                <span className="text-xs text-gray-600 w-full sm:w-auto">Prezzo:</span>
                <div className="relative flex-1 min-w-0 sm:flex-none">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={
                      pendingPriceValues[item.id] ??
                      (item.unit_price !== null && item.unit_price !== undefined
                        ? String(item.unit_price)
                        : '')
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                        setPendingPriceValues(prev => ({
                          ...prev,
                          [item.id]: value,
                        }));
                      }
                    }}
                    onBlur={async () => {
                      const raw =
                        pendingPriceValues[item.id] ??
                        (item.unit_price !== null && item.unit_price !== undefined
                          ? String(item.unit_price)
                          : '');

                      if (raw === '') {
                        setPendingPriceValues(prev => {
                          if (!(item.id in prev)) return prev;
                          const { [item.id]: _removed, ...rest } = prev;
                          return rest;
                        });
                        return;
                      }

                      const parsed = parseFloat(raw.replace(',', '.'));
                      if (isNaN(parsed)) {
                        setPendingPriceValues(prev => {
                          if (!(item.id in prev)) return prev;
                          const { [item.id]: _removed, ...rest } = prev;
                          return rest;
                        });
                        return;
                      }

                      const normalized = Number(parsed.toFixed(4));
                      if (normalized !== Number(item.unit_price ?? 0)) {
                        await handlePriceChange(item.id, normalized);
                      } else {
                        setPendingPriceValues(prev => {
                          if (!(item.id in prev)) return prev;
                          const { [item.id]: _removed, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    className="h-5 text-xs w-full sm:w-16 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-500">€</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[110px]">
                <span className="text-xs text-gray-600 w-full sm:w-auto">Sconto:</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={pendingDiscountValues[item.id] ?? (item.discount_percentage?.toString() ?? '0')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                      setPendingDiscountValues(prev => ({
                        ...prev,
                        [item.id]: value,
                      }));
                    }
                  }}
                  onBlur={async () => {
                    const raw = pendingDiscountValues[item.id] ?? (item.discount_percentage?.toString() ?? '0');
                    const parsed = parseFloat(raw.replace(',', '.'));
                    if (!isNaN(parsed) && parsed !== item.discount_percentage) {
                      await handleDiscountChange(item.id, parsed);
                    } else if (pendingDiscountValues[item.id] !== undefined) {
                      setPendingDiscountValues(prev => {
                        const { [item.id]: _removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-14 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>

              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
                <span className="text-xs text-gray-600 w-full sm:w-auto">IVA:</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={pendingVatValues[item.id] ?? (item.vat_rate?.toString() ?? '22')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                      setPendingVatValues(prev => ({
                        ...prev,
                        [item.id]: value,
                      }));
                    }
                  }}
                  onBlur={async () => {
                    const raw = pendingVatValues[item.id] ?? (item.vat_rate?.toString() ?? '22');
                    const parsed = parseFloat(raw.replace(',', '.'));
                    if (!isNaN(parsed) && parsed !== item.vat_rate) {
                      await handleVatChange(item.id, parsed);
                    } else if (pendingVatValues[item.id] !== undefined) {
                      setPendingVatValues(prev => {
                        const { [item.id]: _removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-14 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>

              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteItem(item.id)}
                className="h-8 w-full sm:h-5 sm:w-5 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Rimuovi"
              >
                <Trash2 className="w-3 h-3" />
                <span className="ml-1 text-xs sm:hidden">Rimuovi riga</span>
              </Button>
            </>
          )}

          {mode === 'view' && (
            <>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:contents">
              <div className="text-xs text-gray-600 sm:min-w-[80px]">
                <div>Qty: {item.quantity} {product?.unit || item.unit || ''}</div>
                <div>Prezzo: €{item.unit_price?.toFixed(2)}</div>
              </div>
              {item.discount_percentage > 0 && (
                <div className="text-xs text-gray-600 sm:min-w-[80px]">
                  <div>Sconto: {item.discount_percentage}%</div>
                </div>
              )}
              <div className="text-xs text-gray-600 sm:min-w-[80px]">
                {(currentInvoice as any)?.no_vat_forfettario ? (
                  <div className="text-amber-700 font-medium">Esente IVA</div>
                ) : (
                  <div>IVA: {item.vat_rate}%</div>
                )}
              </div>
              </div>
            </>
          )}

          <div className="text-xs font-semibold text-gray-900 text-right sm:min-w-[100px]">
            €{itemTotal.toFixed(2)}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Calcola documentType e isTransportDocument solo se currentInvoice esiste
  const documentType = currentInvoice?.document_type as DocumentType | undefined;
  const isTransportDocument = currentInvoice && (documentType === 'ddt' || documentType === 'invoice_with_delivery');

  const documentLabel = currentInvoice
    ? `${getDocumentTypeLabel(documentType || 'invoice')}${currentInvoice.invoice_number ? ` - ${currentInvoice.invoice_number}` : ''}`
    : 'Caricamento...';

  return (
    <>
      <FloatingWindow
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <span className="flex items-baseline gap-2 min-w-0">
            <span className="truncate">{documentLabel}</span>
            {currentInvoice?.customers && (
              <span className="hidden sm:inline text-xs font-normal text-gray-500 truncate">
                {currentInvoice.customers.company_name}
              </span>
            )}
          </span>
        }
        minimizedLabel={documentLabel}
        storageKey="invoice-editor"
      >
        {/* Barra azioni documento */}
        <div className="flex flex-col gap-2 px-2 pt-1.5 pb-1 border-b">
          <div className="flex flex-wrap items-center gap-1">
            {/* L'invio allo SDI non parte piu' da qui: il documento vive su
                Fatture in Cloud ed e' li' che una persona lo trasmette, dopo
                averlo controllato. */}
            {currentInvoice && (currentInvoice as any).fic_id && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs bg-slate-100 text-slate-800 border-slate-300"
                title="Numerato da Fatture in Cloud. L'invio allo SDI si fa da Fatture in Cloud."
              >
                Su Fatture in Cloud
              </span>
            )}
            {canIssue && (
              <Button
                type="button"
                size="sm"
                onClick={() => setConfirmingIssue(true)}
                disabled={issuing}
                className="h-7 text-xs gap-1 bg-slate-800 text-white hover:bg-slate-900"
                title="Crea la fattura su Fatture in Cloud, che assegna il numero"
              >
                {issuing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Emetti su Fatture in Cloud
              </Button>
            )}
            {isClaimed && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs bg-amber-50 text-amber-800 border-amber-300"
                title="La bozza è partita per Fatture in Cloud e la risposta non è arrivata. Controlla su Fatture in Cloud se il documento esiste."
              >
                Invio in corso / esito incerto
              </span>
            )}
            {canUnlock && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleUnlockClaim}
                className="h-7 text-xs gap-1"
              >
                <Unlock className="h-3.5 w-3.5" />
                Sblocca
              </Button>
            )}
            {currentInvoice && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPrintView(true)}
                className="h-7 text-xs gap-1"
              >
                <Printer className="h-3.5 w-3.5" />
                Stampa PDF
              </Button>
            )}
            {currentInvoice
              && !(currentInvoice as any).fic_id
              && !isClaimed
              && currentInvoice.status !== 'cancelled' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDeleteInvoice}
                disabled={deletingInvoice}
                className="h-7 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                title="Elimina documento (solo se non è su Fatture in Cloud)"
              >
                {deletingInvoice ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Elimina
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-2 py-1 overflow-hidden min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : !currentInvoice ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-gray-500">Documento non trovato</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Informazioni Documento Section */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-1 mt-0 mb-1">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="invoice_date" className="text-xs font-medium text-gray-700">
                      Data Documento
                    </Label>
                    <div className="relative">
                      <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                      <Input
                        id="invoice_date"
                        type="text"
                        placeholder="dd/mm/yyyy"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                        onBlur={(e) => handleInvoiceDateChange(e.target.value)}
                        disabled={mode === 'view'}
                        className="h-6 text-xs pl-7"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="payment_terms" className="text-xs font-medium text-gray-700">
                      Modalità di Pagamento
                    </Label>
                    {mode === 'edit' ? (
                      <Select value={paymentTerms || 'none'} onValueChange={handlePaymentTermsChange}>
                        <SelectTrigger className="h-6 text-xs">
                          <SelectValue placeholder="Seleziona modalità" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna</SelectItem>
                          {paymentMethods.map((pm) => (
                            <SelectItem key={pm.id} value={pm.name}>
                              {pm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="payment_terms"
                        type="text"
                        value={paymentTerms || ''}
                        disabled
                        className="h-6 text-xs"
                      />
                    )}
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-gray-700">
                      Stato
                    </Label>
                    <div className="h-6 flex items-center">
                      {currentInvoice ? (
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          currentInvoice.status === 'issued' 
                            ? 'bg-green-100 text-green-800' 
                            : currentInvoice.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {currentInvoice.status === 'issued' ? 'Emessa' : currentInvoice.status === 'draft' ? 'Bozza' : 'Annullata'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Caricamento...</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transport Data Section (only for DDT and Invoice with Delivery) */}
                {isTransportDocument && currentInvoice && (
                  <div className="mt-2 pt-2 border-t border-red-300">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="w-3 h-3 text-red-600" />
                      <Label className="text-xs font-medium text-red-800">Dati Trasporto</Label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {currentInvoice.carriers && (
                        <div>
                          <span className="text-gray-600">Corriere: </span>
                          <span className="font-medium">{currentInvoice.carriers.name}</span>
                        </div>
                      )}
                      {currentInvoice.transport_date && (
                        <div>
                          <span className="text-gray-600">Data trasporto: </span>
                          <span className="font-medium">{currentInvoice.transport_date}</span>
                        </div>
                      )}
                      {currentInvoice.recipient_name && (
                        <div>
                          <span className="text-gray-600">Destinatario: </span>
                          <span className="font-medium">{currentInvoice.recipient_name}</span>
                        </div>
                      )}
                      {currentInvoice.destination_address && (
                        <div className="md:col-span-2">
                          <span className="text-gray-600">Indirizzo destinazione: </span>
                          <span className="font-medium">{currentInvoice.destination_address}</span>
                        </div>
                      )}
                      {currentInvoice.numero_colli && (
                        <div>
                          <span className="text-gray-600">Numero colli: </span>
                          <span className="font-medium">{currentInvoice.numero_colli}</span>
                        </div>
                      )}
                      {currentInvoice.package_types && (
                        <div>
                          <span className="text-gray-600">Tipo collo: </span>
                          <span className="font-medium">{currentInvoice.package_types.name}</span>
                        </div>
                      )}
                      {currentInvoice.peso && (
                        <div>
                          <span className="text-gray-600">Peso: </span>
                          <span className="font-medium">{currentInvoice.peso} kg</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Credit Note Reference Section */}
                {documentType === 'credit_note' && currentInvoice && currentInvoice.reference_invoice_id && (
                  <div className="mt-2 pt-2 border-t border-red-300">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3 h-3 text-red-600" />
                      <Label className="text-xs font-medium text-red-800">Fattura di Riferimento</Label>
                    </div>
                    <div className="text-xs text-gray-600">
                      Fattura n. {currentInvoice.reference_invoice_id}
                      {currentInvoice.credit_note_reason && (
                        <div className="mt-1">
                          <span className="font-medium">Causale: </span>
                          {currentInvoice.credit_note_reason}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 mt-2">
                  <Label htmlFor="notes" className="text-xs font-medium text-gray-700">
                    Note
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Note aggiuntive sul documento..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={(e) => handleNotesChange(e.target.value)}
                    disabled={mode === 'view'}
                    className="min-h-[60px] text-xs resize-none"
                    rows={2}
                  />
                </div>

                {installments.length > 0 && (
                  <div className="md:col-span-2 mt-2 border border-slate-200 rounded-lg bg-slate-50 p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3 h-3 text-slate-700" />
                      <Label className="text-xs font-semibold text-slate-900">Piano Pagamenti</Label>
                    </div>
                    {/* Fatture in Cloud comanda sugli incassi: un segno messo
                        qui verrebbe riportato indietro dalla sincronizzazione. */}
                    {incassiDaFic && (
                      <p className="mb-1 text-[11px] text-slate-600">
                        Gli incassi si segnano su Fatture in Cloud: qui si aggiornano ogni notte.
                      </p>
                    )}
                    <table className="w-full text-[11px]">
                      <thead className="text-slate-900">
                        <tr>
                          <th className="text-center px-1">#</th>
                          <th className="text-left px-1">Descrizione</th>
                          <th className="text-center px-1">Scadenza</th>
                          <th className="text-right px-1">%</th>
                          <th className="text-right px-1">Importo</th>
                          <th className="text-center px-1">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {installments.map((r) => (
                          <tr key={r.id} className="border-t border-slate-100">
                            <td className="text-center px-1 py-0.5">{r.installment_number}</td>
                            <td className="px-1 py-0.5">{r.label || '—'}</td>
                            <td className="text-center px-1 py-0.5">
                              {new Date(r.due_date).toLocaleDateString('it-IT')}
                            </td>
                            <td className="text-right px-1 py-0.5">{r.percentage}%</td>
                            <td className="text-right px-1 py-0.5 font-mono font-semibold">
                              {formatCurrency(Number(r.amount))}
                            </td>
                            <td className="text-center px-1 py-0.5">
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={r.payment_status === 'paid'}
                                  disabled={mode === 'view' || incassiDaFic}
                                  onChange={async (e) => {
                                    const next = e.target.checked;
                                    try {
                                      await markInstallmentPaid(r.id, next);
                                      setInstallments((prev) =>
                                        prev.map((x) =>
                                          x.id === r.id
                                            ? {
                                                ...x,
                                                payment_status: next ? 'paid' : 'pending',
                                                paid_at: next ? new Date().toISOString() : null,
                                              }
                                            : x
                                        )
                                      );
                                    } catch (err: any) {
                                      addNotification({
                                        type: 'error',
                                        title: 'Errore',
                                        message: err.message || 'Aggiornamento rata fallito',
                                      });
                                    }
                                  }}
                                />
                                <span className={r.payment_status === 'paid' ? 'text-green-700' : 'text-gray-600'}>
                                  {r.payment_status === 'paid' ? 'Pagata' : 'In attesa'}
                                </span>
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Righe Documento Section */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-1 mt-0 mb-0 flex-1 flex flex-col min-h-0">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1 flex-shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <h3 className="text-base sm:text-lg font-semibold text-green-800">
                      Righe Documento ({currentInvoice?.invoice_items?.length || 0})
                    </h3>
                  </div>
                  {mode === 'edit' && currentInvoice && (
                    <Button
                      type="button"
                      onClick={() => setShowProductModal(true)}
                      className="h-9 sm:h-7 w-full sm:w-auto text-xs px-3 bg-primary-600 hover:bg-primary-700"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Aggiungi Prodotti
                    </Button>
                  )}
                </div>

                {!currentInvoice ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <p className="text-sm text-green-700">
                      Caricamento documento...
                    </p>
                  </div>
                ) : currentInvoice.invoice_items && currentInvoice.invoice_items.length > 0 ? (
                  <>
                    {/* Controlli di ordinamento e filtro categoria */}
                    <div className="flex items-center gap-2 mb-2 flex-shrink-0 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
                          <Label className="text-xs text-gray-600 flex-shrink-0">Ordina per:</Label>
                          <Select value={sortField} onValueChange={(value: 'code' | 'name') => setSortField(value)}>
                            <SelectTrigger className="h-6 text-xs w-full sm:w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="name">Nome</SelectItem>
                              <SelectItem value="code">Codice</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                            className="h-6 text-xs px-2"
                            title={sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
                          >
                            {sortDirection === 'asc' ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
                          <Label className="text-xs text-gray-600 flex-shrink-0">Categoria:</Label>
                          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="h-6 text-xs w-full sm:w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Tutte le categorie</SelectItem>
                              {Array.from(new Set(currentInvoice.invoice_items.map(item => item.products?.category).filter(Boolean))).map(category => (
                                category ? (
                                  <SelectItem key={category} value={category}>
                                    {category}
                                  </SelectItem>
                                ) : null
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    <div className="flex-1 overflow-y-auto space-y-1 pr-1 min-h-0">
                      {(() => {
                        const filteredItems = currentInvoice.invoice_items
                          ? [...currentInvoice.invoice_items].filter(item => {
                              if (selectedCategory === 'all') return true;
                              return item.products?.category === selectedCategory;
                            })
                          : [];

                        const sortItems = (items: InvoiceItemWithProduct[]) => {
                          return [...items].sort((a, b) => {
                            let comparison = 0;
                            if (sortField === 'code') {
                              comparison = (a.products?.code || '').localeCompare(b.products?.code || '');
                            } else {
                              comparison = (a.description || '').localeCompare(b.description || '');
                            }
                            return sortDirection === 'asc' ? comparison : -comparison;
                          });
                        };

                        return sortItems(filteredItems).map(item => renderProductCard(item));
                      })()}
                    </div>
                    {/* Totali */}
                    <div className="flex-shrink-0 border-t border-green-300 mt-2 pt-2 space-y-2">
                      <div className="flex flex-wrap justify-start sm:justify-end gap-x-4 gap-y-1 text-sm">
                        <div>
                          <span className="text-gray-600">Subtotale: </span>
                          <span className="font-semibold">€{(currentInvoice.subtotal_amount || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Sconto: </span>
                          <span className="font-semibold">€{(currentInvoice.discount_amount || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Imponibile: </span>
                          <span className="font-semibold">€{(currentInvoice.taxable_amount || 0).toFixed(2)}</span>
                        </div>
                        {(currentInvoice as any).no_vat_forfettario ? (
                          <div className="text-amber-700 font-medium">Esente IVA art. 1 c. 54-89 L. 190/2014</div>
                        ) : (
                          <div>
                            <span className="text-gray-600">IVA: </span>
                            <span className="font-semibold">€{(currentInvoice.tax_amount || 0).toFixed(2)}</span>
                          </div>
                        )}
                        {((currentInvoice as any).marca_da_bollo_amount || 0) > 0 && (
                          <div>
                            <span className="text-gray-600">Marca da bollo: </span>
                            <span className="font-semibold">€{((currentInvoice as any).marca_da_bollo_amount || 0).toFixed(2)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">TOTALE: </span>
                          <span className="font-semibold text-lg">€{(currentInvoice.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      {(currentInvoice as any).no_vat_forfettario && (
                        <div className="text-xs text-amber-800 italic bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          {resolveNoVatClauseText((currentInvoice as any).no_vat_clause_text)}
                        </div>
                      )}
                      {(() => {
                        const ibanShown = (currentInvoice as any).iban || COMPANY_INFO.iban;
                        const causale = (currentInvoice as any).causale;
                        return (ibanShown || causale) && (
                          <div className="text-xs text-gray-700 flex flex-wrap gap-x-4 gap-y-1 border-t pt-2">
                            <div className="font-semibold text-slate-800 w-full">DATI BANCARI PER BONIFICO</div>
                            <div><span className="text-gray-500">Intestatario:</span> {COMPANY_INFO.ragioneSociale}</div>
                            {ibanShown && (
                              <div><span className="text-gray-500">IBAN:</span> <span className="font-mono">{ibanShown}</span></div>
                            )}
                            {ibanShown && (
                              <div><span className="text-gray-500">Banca:</span> {COMPANY_INFO.banca}</div>
                            )}
                            {causale && (
                              <div><span className="text-gray-500">Causale:</span> {causale}</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <p className="text-sm text-green-700 mb-4">
                      Nessuna riga in questo documento
                    </p>
                    {mode === 'edit' && (
                      <p className="text-xs text-green-600">
                        Clicca "Aggiungi Prodotti" per iniziare
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end gap-2 border-t bg-gray-50 px-2 py-2 flex-shrink-0">
          <Button
            type="button"
            onClick={handleClose}
            className="h-7 text-xs px-4 bg-green-600 hover:bg-green-700 text-white"
          >
            Chiudi
          </Button>
        </div>
      </FloatingWindow>

      {/* Product Selection Modal */}
      {currentInvoice && (
        <ProductSelectionModal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          mode="select"
          onProductsSelected={handleProductsSelected}
          currentCustomer={currentInvoice.customers || undefined}
          existingProductIds={new Set(currentInvoice.invoice_items.map(item => item.product_id).filter(Boolean) as string[])}
        />
      )}

      {/* Print View Modal */}
      {currentInvoice && (
        <InvoicePrintView
          isOpen={showPrintView}
          onClose={() => setShowPrintView(false)}
          invoiceId={currentInvoice.id}
        />
      )}

      {/* Conferma emissione su Fatture in Cloud */}
      {currentInvoice && (
        <ConfirmDeleteDialog
          open={confirmingIssue}
          onOpenChange={(v) => { if (!issuing) setConfirmingIssue(v); }}
          onConfirm={handleIssueDraft}
          isDeleting={issuing}
          title="Emettere la fattura su Fatture in Cloud?"
          description={`${currentInvoice.customers?.company_name ?? 'Cliente'} — totale ${formatCurrency(currentInvoice.total_amount)}. Il numero lo assegna Fatture in Cloud, con la data di oggi. Una fattura emessa non si annulla: controlla righe e importi prima di confermare. L'invio allo SDI si fa poi da Fatture in Cloud.`}
          confirmLabel="Emetti"
          busyLabel="Emissione..."
          confirmClassName="bg-slate-800 hover:bg-slate-900"
          isAboveFloatingWindow
        />
      )}
    </>
  );
}
